import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  CardActions,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DirectionsIcon from '@mui/icons-material/Directions';
import { projectsApi, tenantApi } from '../services/api';
import { AppContext } from '../App';

const getProjectListFiltersStorageKey = (currentUser) => {
  const userScope = currentUser?.email || currentUser?.id || currentUser?.user_id || 'anonymous';
  return `projects_list_filters_${userScope}`;
};

const readProjectListFilters = (currentUser) => {
  try {
    const raw = sessionStorage.getItem(getProjectListFiltersStorageKey(currentUser));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Could not read project list filters from sessionStorage', error);
    return null;
  }
};

const writeProjectListFilters = (currentUser, nextFilters) => {
  try {
    sessionStorage.setItem(getProjectListFiltersStorageKey(currentUser), JSON.stringify(nextFilters));
  } catch (error) {
    console.warn('Could not write project list filters to sessionStorage', error);
  }
};

function ProjectsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get currentUser from context instead of fetching it again
  const { currentUser } = useContext(AppContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states - only used for Office users
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const hydratedFiltersKeyRef = useRef(null);
  
  // Sort state
  const [sortModel, setSortModel] = useState([
    {
      field: 'created_date',
      sort: 'desc',
    },
  ]);

  // Hide values toggle state - persist in localStorage across pages (Office users only)
  const [hideValues, setHideValues] = useState(() => {
    try {
      const saved = localStorage.getItem('projects_hideValues');
      return saved ? JSON.parse(saved) : false;
    } catch (e) {
      console.warn('Could not read hideValues from localStorage', e);
      return false;
    }
  });

  // Check if user is Office role (only they can see net_total)
  const isOfficeUser = currentUser?.role === 'Office';

  useEffect(() => {
    if (!currentUser?.role) {
      hydratedFiltersKeyRef.current = null;
      setFiltersReady(false);
      setStatusFilter('all');
      setDepartmentFilter('all');
      setShowAllProjects(false);
      setSearchTerm('');
      return;
    }

    const filtersKey = getProjectListFiltersStorageKey(currentUser);
    if (hydratedFiltersKeyRef.current === filtersKey) {
      return;
    }

    const storedFilters = readProjectListFilters(currentUser) || {};

    if (isOfficeUser) {
      setStatusFilter(typeof storedFilters.statusFilter === 'string' ? storedFilters.statusFilter : 'all');
      setDepartmentFilter(
        typeof storedFilters.departmentFilter === 'string'
          ? storedFilters.departmentFilter
          : (currentUser.department || 'all')
      );
      setShowAllProjects(Boolean(storedFilters.showAllProjects));
    } else {
      setStatusFilter('all');
      setDepartmentFilter('all');
      setShowAllProjects(false);
    }

    setSearchTerm('');
    hydratedFiltersKeyRef.current = filtersKey;
    setFiltersReady(true);
  }, [currentUser?.department, currentUser?.email, currentUser?.id, currentUser?.role, currentUser?.user_id, isOfficeUser]);

  useEffect(() => {
    if (!isOfficeUser || !filtersReady || !currentUser?.role) {
      return;
    }

    writeProjectListFilters(currentUser, {
      statusFilter,
      departmentFilter,
      showAllProjects,
    });
  }, [currentUser, departmentFilter, filtersReady, isOfficeUser, showAllProjects, statusFilter]);

  useEffect(() => {
    if (!isOfficeUser || !filtersReady || departments.length === 0) {
      return;
    }

    if (!departmentFilter || departmentFilter === 'all') {
      return;
    }

    const matchingDepartment = departments.find(
      (department) => department.toLowerCase() === departmentFilter.toLowerCase()
    );

    if (matchingDepartment) {
      if (matchingDepartment !== departmentFilter) {
        setDepartmentFilter(matchingDepartment);
      }
      return;
    }

    const defaultDepartment = currentUser?.department
      ? departments.find(
        (department) => department.toLowerCase() === currentUser.department.toLowerCase()
      )
      : null;

    setDepartmentFilter(defaultDepartment || 'all');
  }, [currentUser?.department, departmentFilter, departments, filtersReady, isOfficeUser]);

  // Fetch available departments from tenant branding
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await tenantApi.getBranding();
        const depts = response.data?.branding?.departments;
        if (Array.isArray(depts) && depts.length > 0) {
          setDepartments(depts);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };

    if (isOfficeUser) {
      fetchDepartments();
    }
  }, [isOfficeUser, currentUser?.department]);

  // Helper function to get address string for directions
  const getAddressString = (project) => {
    const parts = [
      project.address_line1,
      project.address_line2,
      project.city,
      project.postcode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Function to open directions in maps
  const openDirections = (project) => {
    const address = getAddressString(project);
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://maps.google.com/maps?q=${encodedAddress}`, '_blank');
    }
  };

  // Fetch projects when filters change or user is loaded
  useEffect(() => {
    const fetchProjects = async () => {
      // Don't fetch until we have user data
      if (!currentUser || (isOfficeUser && !filtersReady)) return;
      
      try {
        setLoading(true);
        
        // Fetch projects based on user role
        let response;
        if (currentUser.role === 'Site' || currentUser.role === 'Site Manager') {
          // Site and Site Manager users always get their assigned Sales Order projects only
          response = await projectsApi.getAll(null, false);
        } else {
          // Office users can use filters
          const status = statusFilter && statusFilter !== 'all' ? statusFilter : null;
          response = await projectsApi.getAll(status, showAllProjects);
        }
        
        setProjects(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
        setLoading(false);
      }
    };

    fetchProjects();
  }, [currentUser, filtersReady, isOfficeUser, statusFilter, showAllProjects]); // Re-fetch when filters change

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleDepartmentFilterChange = (event) => {
    setDepartmentFilter(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleShowAllProjectsChange = (event) => {
    setShowAllProjects(event.target.checked);
  };

  const handleHideValuesChange = (event) => {
    const next = event.target.checked;
    setHideValues(next);
    try {
      localStorage.setItem('projects_hideValues', JSON.stringify(next));
    } catch (e) {
      console.warn('Could not write hideValues to localStorage', e);
    }
  };

  const getFilteredProjects = () => {
    let filtered = projects;

    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter((project) => project.department === departmentFilter);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((project) => {
        return (
          (project.project_id && project.project_id.toString().toLowerCase().includes(searchLower)) ||
          (project.project_no && project.project_no.toString().toLowerCase().includes(searchLower)) ||
          (project.organisation_name && project.organisation_name.toLowerCase().includes(searchLower)) ||
          (project.quote_reference && project.quote_reference.toLowerCase().includes(searchLower)) ||
          (project.address_line1 && project.address_line1.toLowerCase().includes(searchLower)) ||
          (project.city && project.city.toLowerCase().includes(searchLower)) ||
          (project.postcode && project.postcode.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  };

  const getStatusChip = (status) => {
    let color;
    switch (status) {
      case 'Quote':
        color = 'warning';
        break;
      case 'Sales Order':
        color = 'success';
        break;
      case 'Completed':
        color = 'primary';
        break;
      case 'Cancelled':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };

  // Mobile-first responsive columns
  const columns = [
    { 
      field: 'project_id', 
      headerName: 'ID', 
      width: 80,
      hide: isMobile,
      valueGetter: (params) => params.row.project_no || `#${params.row.project_id}`
    },
    { 
      field: 'quote_reference', 
      headerName: 'Quote Ref', 
      flex: isMobile ? 0 : 0.45, 
      minWidth: isMobile ? 0 : 170,
      hide: isMobile,
      valueGetter: (params) => params.row.quote_reference || `#${params.row.project_no || params.row.project_id}`
    },
    { 
      field: 'organisation_name', 
      headerName: 'Organisation', 
      flex: isMobile ? 1 : 0.45, 
      minWidth: isMobile ? 150 : 190 
    },
    { 
      field: 'address', 
      headerName: 'Address', 
      flex: isMobile ? 0 : 0.65, 
      minWidth: isMobile ? 0 : 220,
      hide: isMobile,
      valueGetter: (params) => 
        `${params.row.address_line1}, ${params.row.city}, ${params.row.postcode}`
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: isMobile ? 100 : 140,
      renderCell: (params) => getStatusChip(params.value)
    },
    { 
      field: 'created_date', 
      headerName: 'Date', 
      width: isMobile ? 90 : 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return isMobile ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) : date.toLocaleDateString('en-GB');
      }
    },
    { 
      field: 'net_total', 
      headerName: 'Net Total', 
      width: 120,
      valueFormatter: (params) => {
        const total = params.value || 0;
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(total);
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: isMobile ? 70 : 90,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/projects/${params.row.project_id}`)}
        >
          {isMobile ? 'View' : 'View'}
        </Button>
      ),
    },
  ];

  // Filter columns based on hideValues state and user role
  const filteredColumns = columns.filter(col => {
    // Only Office users can see net_total column
    if (col.field === 'net_total') {
      if (!isOfficeUser) return false;
      if (hideValues) return false;
    }
    return true;
  });

  // Mobile card view component
  const ProjectCard = ({ project }) => (
    <Card sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/projects/${project.project_id}`)}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {project.organisation_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                openDirections(project);
              }}
              color="primary"
              sx={{ p: 0.5 }}
            >
              <DirectionsIcon fontSize="small" />
            </IconButton>
            {getStatusChip(project.status)}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {project.quote_reference ? `Quote: ${project.quote_reference}` : `Project #${project.project_no || project.project_id}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {project.address_line1}, {project.city}, {project.postcode}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(project.created_date).toLocaleDateString('en-GB')}
          </Typography>
          {isOfficeUser && !hideValues && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(project.net_total || 0)}
            </Typography>
          )}
        </Box>
      </CardContent>
      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
        <Button size="small" variant="outlined" fullWidth>
          View Details
        </Button>
      </CardActions>
    </Card>
  );

  const filteredProjects = getFilteredProjects();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', xl: 1600 },
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Box sx={{ 
        mb: { xs: 2, md: 4 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {(currentUser?.role === 'Site' || currentUser?.role === 'Site Manager') ? 'My Projects' : 'Projects'}
        </Typography>
        {/* Only Office users can create new quotes */}
        {currentUser?.role === 'Office' && (
          <Button
            variant="contained"
            component={RouterLink}
            to="/new-quote"
            color="primary"
            fullWidth={isMobile}
          >
            New Quote
          </Button>
        )}
      </Box>

      {/* Only show filters for Office users */}
      {currentUser?.role === 'Office' && (
        <Paper sx={{ p: { xs: 2, md: 2 }, mb: { xs: 2, md: 4 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search projects"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by ID, name, quote ref, address..."
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Quote">Quotes</MenuItem>
                  <MenuItem value="Sales Order">Sales Orders</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {departments.length > 0 && (
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="department-filter-label">Department</InputLabel>
                  <Select
                    labelId="department-filter-label"
                    value={departmentFilter}
                    onChange={handleDepartmentFilterChange}
                    label="Department"
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllProjects}
                    onChange={handleShowAllProjectsChange}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Show all</Typography>}
              />
            </Grid>
            {isOfficeUser && (
              <Grid item xs={6} sm={3} md={2.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideValues}
                      onChange={handleHideValuesChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{hideValues ? 'Show values' : 'Hide values'}</Typography>}
                />
              </Grid>
            )}
          </Grid>
          
        </Paper>
      )}

      {/* Simple search for Site and Site Manager users */}
      {(currentUser?.role === 'Site' || currentUser?.role === 'Site Manager') && (
        <Paper sx={{ p: 2, mb: { xs: 2, md: 4 } }}>
          <TextField
            fullWidth
            label="Search projects"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by ID, name, quote ref, address..."
            variant="outlined"
            size="small"
          />
        </Paper>
      )}

      {/* Mobile view - Cards */}
      <Box>
        {isMobile ? (
          <Box>
            {filteredProjects.map((project) => (
              <ProjectCard key={project.project_id} project={project} />
            ))}
            {filteredProjects.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No projects found
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          /* Desktop view - DataGrid */
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                  rows={filteredProjects}
                  columns={filteredColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  getRowId={(row) => row.project_id}
                  sortModel={sortModel}
                  onSortModelChange={(model) => setSortModel(model)}
                  disableSelectionOnClick
                  loading={loading}
                  sx={{
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,135,193,0.04)' : 'rgba(0,135,193,0.08)',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
}

export default ProjectsList;
